<script setup lang="ts">
import { ref, watch } from "vue";
import { UiButton, UiDialog, UiInfoBox, UiText, UiTextarea } from "@shopexperts/shared";
import type { AdminWalletTransaction } from "@shopexperts/legacy-api";
import { useDeleteAdminWalletTransaction } from "@/app/composables/wallet";

const props = defineProps<{
  transaction: AdminWalletTransaction | null;
  organizationId?: number | null;
}>();

const emit = defineEmits<{
  (e: "deleted"): void;
}>();

const open = defineModel<boolean>({ default: false });
const { mutateAsync: deleteTransaction, isPending } = useDeleteAdminWalletTransaction();
const deleteNote = ref("");

watch(open, (isOpen) => {
  if (isOpen) deleteNote.value = "";
});

async function submit() {
  if (!props.transaction) return;

  try {
    await deleteTransaction({
      transactionId: props.transaction.id,
      organizationId: props.organizationId ?? props.transaction.organization_id,
      body: { delete_note: deleteNote.value.trim() || null },
    });
    open.value = false;
    emit("deleted");
  } catch {
    // Error toast handled by the composable.
  }
}

function cancel() {
  open.value = false;
}
</script>

<template>
  <UiDialog v-model="open" v-model:loading="isPending" title="Delete wallet transaction" size="lg">
    <div class="flex flex-col gap-5">
      <UiInfoBox variant="warning" title="This reverses wallet balances">
        <UiText as="span" variant="p-small">
          <span>
            Only the latest admin-created transaction can be deleted during the backend time window. The row remains
            visible to admins as a deleted audit record.
          </span>
        </UiText>
      </UiInfoBox>

      <div class="rounded-lg border border-se-gray-200 bg-se-gray-50 px-4 py-3">
        <UiText
          as="p"
          variant="p-small"
          class="font-semibold capitalize text-se-dark"
          :text="transaction ? `#${transaction.id} ${transaction.transaction_kind.replace(/_/g, ' ')}` : 'No transaction selected'"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Admin deletion note" />
        <UiTextarea
          v-model="deleteNote"
          :rows="3"
          placeholder="Optional reason shown on the deleted audit record."
          :disabled="isPending"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="isPending" @click="cancel" />
        <UiButton
          variant="primary"
          :label="isPending ? 'Deleting...' : 'Delete transaction'"
          :disabled="isPending || !transaction"
          @click="submit"
        />
      </div>
    </template>
  </UiDialog>
</template>
